import { getApiUrl } from 'utils/config';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`
});

/** Thrown when POST /plaid/complete-link returns 409 ITEM_ALREADY_LINKED.
 *  Callers should prompt the user (Reconnect vs Replace) rather than retrying blindly. */
export class PlaidDuplicateItemError extends Error {
  existingItemId: string;
  institutionId: string;
  institutionName: string;
  constructor(existingItemId: string, institutionId: string, institutionName: string) {
    super(`${institutionName} is already linked`);
    this.name = 'PlaidDuplicateItemError';
    this.existingItemId = existingItemId;
    this.institutionId = institutionId;
    this.institutionName = institutionName;
  }
}

/** Thrown when the backend surfaces a Plaid item-auth error (ITEM_LOGIN_REQUIRED, etc.).
 *  The UI should offer Reconnect (update mode) for `itemId`. */
export class PlaidItemAuthError extends Error {
  code: string;
  itemId: string | null;
  constructor(code: string, itemId: string | null, message?: string) {
    super(message || `Plaid item error ${code}`);
    this.name = 'PlaidItemAuthError';
    this.code = code;
    this.itemId = itemId;
  }
}

const ITEM_AUTH_CODES = new Set([
  'ITEM_LOGIN_REQUIRED',
  'PENDING_EXPIRATION',
  'INVALID_CREDENTIALS',
  'USER_PERMISSION_REVOKED',
]);

export const plaidService = {
  // Create link token
  createLinkToken: async () => {
    const response = await fetch(`${getApiUrl()}/plaid/link-token`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body?.detail ?? response.statusText ?? 'Failed to create link token';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }

    return response.json();
  },

  // Complete link: exchange + initial sync on backend; returns only item_id (access_token never sent to client).
  // Set force=true to explicitly replace an already-linked institution (destroys the old item's transactions/investments).
  completeLink: async (publicToken: string, force: boolean = false): Promise<{ item_id: string }> => {
    const response = await fetch(`${getApiUrl()}/plaid/complete-link`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        public_token: publicToken,
        force,
      })
    });

    if (response.status === 409) {
      const body = await response.json().catch(() => ({}));
      const detail = body?.detail ?? {};
      if (detail?.code === 'ITEM_ALREADY_LINKED') {
        throw new PlaidDuplicateItemError(
          detail.existing_item_id,
          detail.institution_id,
          detail.institution_name,
        );
      }
      if (typeof detail?.code === 'string' && ITEM_AUTH_CODES.has(detail.code)) {
        throw new PlaidItemAuthError(detail.code, detail.item_id ?? null, detail.message);
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body?.detail ?? response.statusText ?? 'Failed to complete bank connection';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }

    return response.json();
  },

  // Create an update-mode link token for an existing item so the user can re-authenticate in place
  // (no new Item is created, so no history is lost).
  createUpdateLinkToken: async (itemId: string): Promise<{ link_token: string; expiration: string }> => {
    const response = await fetch(`${getApiUrl()}/plaid/items/${itemId}/link-token`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body?.detail ?? response.statusText ?? 'Failed to prepare reconnect flow';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return response.json();
  },

  // Sync accounts
  syncAccounts: async (accessToken: string) => {
    const response = await fetch(`${getApiUrl()}/plaid/sync-accounts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        access_token: accessToken
      })
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body?.detail ?? response.statusText ?? 'Failed to sync accounts';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }

    return response.json();
  },

  // Sync transactions. For large date ranges (e.g. 2 years) backend returns 202 Accepted and syncs in background.
  syncTransactions: async (accessToken: string, startDate?: string, endDate?: string): Promise<{ status?: string; message?: string; start_date?: string; end_date?: string } | unknown[]> => {
    const payload: Record<string, string> = {
      access_token: accessToken,
    };

    if (startDate) {
      payload.start_date = startDate;
    }
    if (endDate) {
      payload.end_date = endDate;
    }

    const response = await fetch(`${getApiUrl()}/plaid/sync-transactions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to sync transactions');
    }

    const data = await response.json();
    // 202 Accepted = sync running in background for large range
    if (response.status === 202 && data?.status === 'accepted') {
      return data as { status: string; message: string; start_date?: string; end_date?: string };
    }
    return data as unknown[];
  },

  // Sync transactions for an existing item using stored access token (full history via Plaid cursor sync).
  // Optional timeoutMs (default 5 min) to allow full history sync without request timeout.
  syncItemTransactions: async (
    itemId: string,
    timeoutMs: number = 300000,
  ): Promise<{ synced: number; added: number; modified: number; removed: number; update_status?: string | null }> => {
    const url = `${getApiUrl()}/plaid/items/${itemId}/sync-transactions`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to sync transactions for this connection');
      }

      return response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  },

  // Sync investments
  syncInvestments: async (accessToken: string) => {
    const response = await fetch(`${getApiUrl()}/plaid/sync-investments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        access_token: accessToken
      })
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body?.detail ?? response.statusText ?? 'Failed to sync investments';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }

    return response.json();
  },

  // Sync accounts for an existing item using stored access token
  syncItemAccounts: async (itemId: string) => {
    const response = await fetch(`${getApiUrl()}/plaid/items/${itemId}/sync`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to sync accounts for this connection');
    }

    return response.json();
  },

  // Get user items
  getUserItems: async () => {
    const response = await fetch(`${getApiUrl()}/plaid/items`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to get user items');
    }
    
    return response.json();
  },

  // Get user accounts
  getUserAccounts: async () => {
    const response = await fetch(`${getApiUrl()}/plaid/accounts`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to get user accounts');
    }
    
    return response.json();
  },

  // Get user transactions
  getUserTransactions: async (limit = 100) => {
    const response = await fetch(`${getApiUrl()}/plaid/transactions?limit=${limit}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to get user transactions');
    }
    
    return response.json();
  },

  // Get user investments
  getUserInvestments: async () => {
    const response = await fetch(`${getApiUrl()}/plaid/investments`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to get user investments');
    }
    
    return response.json();
  },

  // Soft-delete a Plaid item. Server keeps the Plaid access_token alive so restoreItem is possible
  // without re-Link. Does NOT free a Plaid Trial slot (the 10-Item cap counts lifetime Items).
  deletePlaidItem: async (itemId: string) => {
    const response = await fetch(`${getApiUrl()}/plaid/items/delete/${itemId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to delete Plaid item');
    }

    return response.json();
  },

  // Permanently delete a soft-deleted item at Plaid AND locally. The item must be hidden first.
  // Under Trial billing the lifetime slot is NOT returned by this action.
  hardDeletePlaidItem: async (itemId: string) => {
    const response = await fetch(`${getApiUrl()}/plaid/items/${itemId}/permanent`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body?.detail ?? response.statusText ?? 'Failed to permanently delete';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return response.json();
  },

  // Undo a soft delete: rebuilds domain accounts/transactions/investments from the preserved
  // plaid_* rows. No Plaid API calls, no slot cost.
  restoreItem: async (itemId: string) => {
    const response = await fetch(`${getApiUrl()}/plaid/items/${itemId}/restore`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message = body?.detail ?? response.statusText ?? 'Failed to restore Plaid item';
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return response.json();
  },

  // Get transactions by date range
  getTransactionsByDateRange: async (startDate: string, endDate: string) => {
    const response = await fetch(
      `${getApiUrl()}/plaid/transactions/date-range?start_date=${startDate}&end_date=${endDate}`,
      {
        headers: getAuthHeaders()
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to get transactions by date range');
    }
    
    return response.json();
  }
}; 